import moment from 'moment-timezone';

import api from '@/helpers/api';
import { serializeArrayParams } from '@/helpers/serializeArrayParams';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import { mapCandidateWorkersApiResponseToPaginatedCandidates } from '@/modules/companyNeed/helpers/candidateWorkersMapper';
import { mapCompanyNeedDetailsResponseToCompanyNeed } from '@/modules/companyNeed/helpers/companyNeedMapper';
import { mapCompanyNeedsQuickActionToModalItems } from '@/modules/companyNeed/helpers/move';
import { MOVE_COMMAND_TO_WORKER_STATUS } from '@/modules/companyNeed/helpers/recruitmentKanban';
import {
	extractWorkerInterviews,
	pickLatestWorkerInterview,
} from '@/modules/companyNeed/helpers/workerInterview';
import { RangeType, UnavailabilityType } from '@/modules/companyNeed/types';
import { useConfigurationStore } from '@/store/configurationStore';

import type {
	ArchiveCompanyNeedResponse,
	CompanyNeed,
	CompanyNeedByCompany,
	CompanyNeedDetailsResponse,
	CompanyNeedListQueryParams,
	CompanyNeedMetadata,
	CompanyNeedPaginatedResponse,
	CompanyNeedPopulated,
	CompanyNeedQuickActionResponse,
	CompanyNeedWorkersResponse,
	CreateCompanyNeed,
	CreateCompanyNeedResponse,
	DuplicateCompanyNeedResponse,
	ReopenCompanyNeedResponse,
	Unavailability,
	UpdateCompanyNeed,
	UpdateCompanyNeedResponse,
	WorkerInterview,
	WorkersQueryParams,
} from '@/modules/companyNeed/types';
import type { ArchivedCompanyNeed } from '@/modules/companyNeed/types/archived';
import type { CompanyNeedModal } from '@/modules/companyNeed/types/modals';
import type {
	MoveWorker,
	MoveWorkerCompanyNeedCommands,
} from '@/modules/companyNeed/types/moveWorker';
import type {
	AddWorkersToCompanyNeed,
	AddWorkersToCompanyNeedResponse,
	UpdateCompanyNeedWorkerStatus,
	UpdateCompanyNeedWorkerStatusResponse,
} from '@/modules/companyNeed/types/worker';
import type {
	CandidatesQueryParams,
	CandidateWorker,
} from '@/modules/companyNeed/views/details/candidates/types';
import type {
	MatchingWorkerItemDto,
	MatchingWorkersMetadata,
	MatchingWorkersMetadataQueryParams,
	MatchingWorkersQueryParams,
} from '@/modules/companyNeed/views/details/matching/types';
import type {
	MoveWorkersToPoolData,
	PoolWorkerAgency,
	PoolWorkerDto,
	PoolWorkersQueryParams,
	PoolWorkerUnavailability,
	WorkerPool,
} from '@/modules/companyNeed/views/details/pool/types';
import type {
	RejectedProfileDto,
	RejectedProfileQuery,
} from '@/modules/companyNeed/views/details/rejected-profiles/types';

export const PAGE_SIZE = 25;

// --- CompanyNeed CRUD ---

const fetchCompanyNeedsList = async (
	params: CompanyNeedListQueryParams
): Promise<CompanyNeedPaginatedResponse<CompanyNeedPopulated>> => {
	const response = await api.request({
		url: '/besoins',
		method: 'GET',
		params,
	});
	return response.data;
};

const fetchCompanyNeedsMetadata = async (
	params: Omit<CompanyNeedListQueryParams, 'page' | 'limit' | 'sort' | 'order'>
): Promise<CompanyNeedMetadata> => {
	const response = await api.request({
		url: '/besoins/metadata',
		method: 'GET',
		params,
	});
	return response.data;
};

const fetchCompanyNeedById = async (
	companyNeedId: string
): Promise<CompanyNeed> => {
	const response = await api.request<CompanyNeedDetailsResponse>({
		url: `/besoins/${companyNeedId}`,
		method: 'GET',
	});
	return mapCompanyNeedDetailsResponseToCompanyNeed(response.data);
};

const createCompanyNeed = async (
	companyNeed: CreateCompanyNeed
): Promise<CreateCompanyNeedResponse> => {
	const response = await api.request({
		url: '/besoins',
		method: 'POST',
		data: companyNeed,
	});
	return response.data;
};

const updateCompanyNeed = async (
	companyNeedId: string,
	companyNeed: UpdateCompanyNeed
): Promise<UpdateCompanyNeedResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}`,
		method: 'PUT',
		data: companyNeed,
	});
	return response.data;
};

const archiveCompanyNeed = async (
	companyNeedId: string
): Promise<ArchiveCompanyNeedResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/archive`,
		method: 'PATCH',
	});
	return response.data;
};

const reopenCompanyNeed = async (
	companyNeedId: string
): Promise<ReopenCompanyNeedResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/reopen`,
		method: 'PATCH',
	});
	return response.data;
};

const duplicateCompanyNeed = async (
	companyNeedId: string
): Promise<DuplicateCompanyNeedResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/duplicate`,
		method: 'POST',
	});
	return response.data;
};

const deleteCompanyNeed = async (companyNeedId: string): Promise<void> => {
	await api.request({ url: `/besoins/${companyNeedId}`, method: 'DELETE' });
};

const fetchArchivedCompanyNeedsForCompany = async (
	companyId: string
): Promise<ArchivedCompanyNeed[]> => {
	const response = await api.request({
		url: `/besoins/company/${companyId}/archived`,
		method: 'GET',
	});
	return response.data;
};

const fetchCompanyNeedsByCompanyId = async (
	companyId: string,
	params: CompanyNeedListQueryParams
): Promise<CompanyNeedPaginatedResponse<CompanyNeedByCompany>> => {
	const response = await api.request({
		url: `/besoins/company/${companyId}`,
		method: 'GET',
		params,
	});
	return response.data;
};

const fetchCompanyNeedsForModal = async (
	besoinId?: string,
	search?: string,
	workers: MoveWorker[] = []
): Promise<CompanyNeedModal[]> => {
	const workerIds = workers.map((worker) => worker.id).join(',');
	const response = await api.request<CompanyNeedQuickActionResponse[]>({
		url: '/besoins/quick-actions',
		method: 'GET',
		params: {
			...(besoinId ? { besoinId } : {}),
			...(search ? { search } : {}),
			...(workerIds ? { workerIds } : {}),
		},
	});
	return mapCompanyNeedsQuickActionToModalItems(response.data);
};

// --- Workers ---

const addWorkersToCompanyNeed = async (
	companyNeedId: string,
	data: AddWorkersToCompanyNeed
): Promise<AddWorkersToCompanyNeedResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/workers`,
		method: 'POST',
		data,
	});
	return response.data;
};

const updateCompanyNeedWorkerStatus = async (
	companyNeedId: string,
	workerId: string,
	data: UpdateCompanyNeedWorkerStatus
): Promise<UpdateCompanyNeedWorkerStatusResponse> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/workers/${workerId}/status`,
		method: 'PATCH',
		data,
	});
	return response.data;
};

const updateProfilesStatus = async (
	companyNeedId: string,
	workerIds: string[],
	command: MoveWorkerCompanyNeedCommands
) => {
	const workerStatus = MOVE_COMMAND_TO_WORKER_STATUS[command];
	if (!workerStatus) return;

	await Promise.all(
		workerIds.map((workerId) =>
			updateCompanyNeedWorkerStatus(companyNeedId, workerId, {
				status: workerStatus,
			})
		)
	);

	await fetchCompanyNeedById(companyNeedId);
};

const moveWorkersToPool = async (
	companyNeedId: string,
	data: MoveWorkersToPoolData
): Promise<void> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/pool/workers`,
		method: 'POST',
		data,
	});
	return response.data;
};

const removeWorkersFromPool = async (
	companyNeedId: string,
	workerIds: string[]
): Promise<void> => {
	await api.request({
		url: `/besoins/${companyNeedId}/pool/workers`,
		method: 'DELETE',
		data: workerIds,
	});
};

// --- Matching ---

const fetchMatchingWorkersForCompanyNeed = async (
	companyNeedId: string,
	params: MatchingWorkersQueryParams
): Promise<CompanyNeedPaginatedResponse<MatchingWorkerItemDto>> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/matching-workers`,
		method: 'GET',
		params,
		paramsSerializer: () =>
			serializeArrayParams(params as Record<string, unknown>),
	});
	return response.data;
};

const fetchMatchingWorkersMetadataForCompanyNeed = async (
	companyNeedId: string,
	params: MatchingWorkersMetadataQueryParams
): Promise<MatchingWorkersMetadata> => {
	const response = await api.request<Partial<MatchingWorkersMetadata>>({
		url: `/besoins/${companyNeedId}/matching-workers/metadata`,
		method: 'GET',
		params,
		paramsSerializer: () =>
			serializeArrayParams(params as Record<string, unknown>),
	});
	return {
		keywords: response.data.keywords ?? [],
		cities: response.data.cities ?? [],
	};
};

const fetchFlexibleMatchingWorkersForCompanyNeed = async (
	companyNeedId: string,
	params: MatchingWorkersQueryParams
): Promise<CompanyNeedPaginatedResponse<MatchingWorkerItemDto>> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/matching-workers-flexible`,
		method: 'GET',
		params,
		paramsSerializer: () =>
			serializeArrayParams(params as Record<string, unknown>),
	});
	return response.data;
};

// --- Pool ---

const mapApiUnavailabilityTypeToUi = (
	apiType: PoolWorkerUnavailability['unavailabilityType']
): UnavailabilityType =>
	apiType === 'mission'
		? UnavailabilityType.MISSION
		: UnavailabilityType.DEFAULT;

const getRangeTypeForDisplay = (
	unavailabilityType: UnavailabilityType,
	isInProgress: boolean
): RangeType => {
	if (unavailabilityType === UnavailabilityType.MISSION) {
		return isInProgress ? RangeType.FROM : RangeType.TO;
	}
	return isInProgress ? RangeType.TO : RangeType.FROM;
};

const pickPoolSegmentForDisplay = (
	segments: PoolWorkerUnavailability[]
): PoolWorkerUnavailability | null => {
	if (segments.length === 0) {
		return null;
	}

	const now = moment();
	const withBounds = segments.filter(
		(
			segment
		): segment is PoolWorkerUnavailability & {
			fromDate: string;
			toDate: string;
		} => Boolean(segment.fromDate && segment.toDate)
	);

	if (withBounds.length === 0) {
		return null;
	}

	const inProgress = withBounds.filter((segment) => {
		const fromDay = moment(segment.fromDate).startOf('day');
		const toEnd = moment(segment.toDate).endOf('day');
		const t = now.valueOf();
		return t >= fromDay.valueOf() && t <= toEnd.valueOf();
	});

	if (inProgress.length > 0) {
		return inProgress[0];
	}

	const startOfToday = now.clone().startOf('day');
	const upcoming = withBounds.filter((segment) =>
		moment(segment.fromDate).startOf('day').isAfter(startOfToday)
	);

	if (upcoming.length === 0) {
		return null;
	}

	return upcoming.reduce((earliest, segment) => {
		const currentFrom = moment(segment.fromDate).valueOf();
		const earliestFrom = moment(earliest.fromDate).valueOf();
		return currentFrom < earliestFrom ? segment : earliest;
	});
};

const mapPoolSegmentToUnavailability = (
	unavailability: PoolWorkerUnavailability
): Unavailability | null => {
	const uiType = mapApiUnavailabilityTypeToUi(
		unavailability.unavailabilityType
	);
	const now = moment();
	const fromDate = unavailability.fromDate
		? moment(unavailability.fromDate)
		: null;
	const toDate = unavailability.toDate ? moment(unavailability.toDate) : null;

	if (fromDate && toDate) {
		const fromDay = fromDate.clone().startOf('day');
		const toEnd = toDate.clone().endOf('day');
		const t = now.valueOf();
		const isInProgress = t >= fromDay.valueOf() && t <= toEnd.valueOf();

		if (isInProgress) {
			return {
				unvailabilityType: uiType,
				rangeType: getRangeTypeForDisplay(uiType, true),
				date: toDate.valueOf(),
			};
		}

		const isUpcoming = fromDay.isAfter(now.clone().startOf('day'));
		if (isUpcoming) {
			return {
				unvailabilityType: uiType,
				rangeType: getRangeTypeForDisplay(uiType, false),
				date: fromDate.valueOf(),
			};
		}

		return null;
	}

	return {
		unvailabilityType: uiType,
		rangeType: fromDate ? RangeType.FROM : RangeType.TO,
		date: fromDate?.valueOf() ?? toDate?.valueOf() ?? 0,
	};
};

const mapPoolUnavailability = (
	unavailability: PoolWorkerDto['unavailabilities']
): Unavailability | null => {
	if (!unavailability || unavailability.length === 0) {
		return null;
	}

	const segment = pickPoolSegmentForDisplay(unavailability);
	if (!segment) {
		return null;
	}

	return mapPoolSegmentToUnavailability(segment);
};

const mapPoolWorkerAgencyDto = (
	agency: PoolWorkerAgency
): PoolWorkerAgency => ({
	agencyId: agency.agencyId,
	agencyName: agency.agencyName ?? null,
	publicName: agency.publicName ?? null,
});

const mapPoolWorkerDtoToWorkerPool = (dto: PoolWorkerDto): WorkerPool => ({
	workerId: dto.workerId,
	firstname: dto.firstname,
	lastname: dto.lastname,
	avatarUrl: dto.avatarUrl,
	delegable: dto.delegable ?? true,
	registered: dto.registered ?? false,
	lastUpdate: dto.lastUpdate ? moment(dto.lastUpdate).toDate() : null,
	phone: dto.phone ?? '',
	email: dto.email ?? '',
	missionWithClient: dto.missionWithClient ?? 0,
	distance: dto.distance ?? 0,
	refusalReason: dto.refusalReason,
	experienceLevel: normalizeJobExperience(dto.experienceLevel),
	unavailability: mapPoolUnavailability(dto.unavailabilities ?? []),
	history: dto.following,
	ongoingProposals: dto.ongoingProposals ?? [],
	agencyName: dto.agencyName ?? null,
	agencies: dto.agencies?.map(mapPoolWorkerAgencyDto),
});

type PoolWorkersApiResponse =
	| PoolWorkerDto[]
	| {
			items?: PoolWorkerDto[];
			data?: PoolWorkerDto[];
			offset?: number;
			limit?: number;
			total?: number;
			page?: number;
	  };

const mapPoolWorkersApiResponseToPaginated = (
	response: PoolWorkersApiResponse,
	params: PoolWorkersQueryParams
): CompanyNeedPaginatedResponse<WorkerPool> => {
	const isArray = Array.isArray(response);
	const rawItems = isArray ? response : (response.items ?? response.data ?? []);
	const limit =
		(isArray ? undefined : response.limit) ?? params.limit ?? rawItems.length;
	const offset = (isArray ? undefined : response.offset) ?? params.offset ?? 0;
	const total = (isArray ? undefined : response.total) ?? rawItems.length;
	const page =
		(isArray ? undefined : response.page) ??
		(limit > 0 ? Math.floor(offset / limit) + 1 : 1);
	const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;

	return {
		items: rawItems.map(mapPoolWorkerDtoToWorkerPool),
		offset,
		limit,
		page,
		total,
		totalPages,
		hasNextPage: offset + limit < total,
		hasPreviousPage: offset > 0,
	};
};

enum MissionAddWorkerWorkersPath {
	PoolWorkers = 'pool-workers',
	OngoingProposalWorkers = 'ongoing-proposal-workers',
}

const fetchMissionAddWorkerWorkers = async (
	companyNeedId: string,
	workersPath: MissionAddWorkerWorkersPath,
	params?: PoolWorkersQueryParams
): Promise<CompanyNeedPaginatedResponse<WorkerPool>> => {
	const response = await api.request<PoolWorkersApiResponse>({
		url: `/besoins/${companyNeedId}/${workersPath}`,
		method: 'GET',
		params,
		paramsSerializer: params
			? () => serializeArrayParams(params as Record<string, unknown>)
			: undefined,
	});
	return mapPoolWorkersApiResponseToPaginated(response.data, params ?? {});
};

const fetchPaginatedPoolWorkers = async (
	companyNeedId: string,
	params?: PoolWorkersQueryParams
): Promise<CompanyNeedPaginatedResponse<WorkerPool>> =>
	fetchMissionAddWorkerWorkers(
		companyNeedId,
		MissionAddWorkerWorkersPath.PoolWorkers,
		params
	);

const fetchOngoingProposalWorkers = async (
	companyNeedId: string,
	params?: PoolWorkersQueryParams
): Promise<CompanyNeedPaginatedResponse<WorkerPool>> =>
	fetchMissionAddWorkerWorkers(
		companyNeedId,
		MissionAddWorkerWorkersPath.OngoingProposalWorkers,
		params
	);

// --- Recruitment ---

type RawRecruitmentWorker = Omit<
	CompanyNeedWorkersResponse['prequalified'][number],
	'interview' | 'interviews'
> & {
	interview?: WorkerInterview | WorkerInterview[] | null;
	interviews?: WorkerInterview[] | null;
};

type RawCompanyNeedWorkersResponse = {
	[Key in keyof CompanyNeedWorkersResponse]: RawRecruitmentWorker[];
};

const RECRUITMENT_WORKER_KEYS = [
	'prequalified',
	'contacted',
	'interview',
	'proposedToCompany',
	'interviewCompany',
	'missionProposed',
] as const satisfies readonly (keyof CompanyNeedWorkersResponse)[];

const normalizeRecruitmentWorker = (
	worker: RawRecruitmentWorker
): CompanyNeedWorkersResponse['prequalified'][number] => {
	const interviews = extractWorkerInterviews(worker);

	return {
		...worker,
		interviews,
		interview: pickLatestWorkerInterview(interviews),
	};
};

const normalizeCompanyNeedWorkersResponse = (
	data: RawCompanyNeedWorkersResponse
): CompanyNeedWorkersResponse =>
	RECRUITMENT_WORKER_KEYS.reduce((acc, key) => {
		acc[key] = (data[key] ?? []).map(normalizeRecruitmentWorker);
		return acc;
	}, {} as CompanyNeedWorkersResponse);

const fetchWorkersForCompanyNeed = async (
	companyNeedId: string,
	params: WorkersQueryParams
): Promise<CompanyNeedWorkersResponse> => {
	const response = await api.request<RawCompanyNeedWorkersResponse>({
		url: `/besoins/${companyNeedId}/workers`,
		method: 'GET',
		params,
	});
	return normalizeCompanyNeedWorkersResponse(response.data);
};

// --- Rejected profiles ---
const mapRejectedWorkerDtoToRejectedProfileQuery = (
	dto: RejectedProfileDto
): RejectedProfileQuery => {
	const configurationStore = useConfigurationStore();
	const reasonLabel = dto.reasonId
		? configurationStore.rejectedCompanyNeedReasonsMap.get(dto.reasonId)
		: undefined;
	return {
		id: dto.workerId,
		firstname: dto.firstname ?? '',
		lastname: dto.lastname ?? '',
		picture: dto.picture ?? undefined,
		registered: dto.registered,
		refusalDate: dto.refusalDate ? new Date(dto.refusalDate) : new Date(),
		reason: reasonLabel ?? dto.reasonId ?? '',
		comment: dto.comment ?? undefined,
	};
};

const fetchRejectedProfiles = async (
	companyNeedId: string,
	search?: string
): Promise<RejectedProfileQuery[]> => {
	const response = await api.request<RejectedProfileDto[]>({
		url: `/besoins/${companyNeedId}/rejected-workers`,
		method: 'GET',
		params: {
			search: search || undefined,
		},
	});
	return response.data.map(mapRejectedWorkerDtoToRejectedProfileQuery);
};

// --- Candidates ---
const fetchCandidates = async (
	companyNeedId: string,
	params: CandidatesQueryParams
): Promise<CompanyNeedPaginatedResponse<CandidateWorker>> => {
	const response = await api.request({
		url: `/besoins/${companyNeedId}/candidate-workers-enriched`,
		method: 'GET',
		params,
	});
	return mapCandidateWorkersApiResponseToPaginatedCandidates(
		response.data,
		params
	);
};

export default {
	fetchCompanyNeedsList,
	fetchCompanyNeedsMetadata,
	fetchCompanyNeedById,
	fetchCompanyNeedsByCompanyId,
	createCompanyNeed,
	updateCompanyNeed,
	archiveCompanyNeed,
	reopenCompanyNeed,
	duplicateCompanyNeed,
	deleteCompanyNeed,
	fetchArchivedCompanyNeedsForCompany,
	fetchCompanyNeedsForModal,
	addWorkersToCompanyNeed,
	updateCompanyNeedWorkerStatus,
	updateProfilesStatus,
	moveWorkersToPool,
	removeWorkersFromPool,
	fetchMatchingWorkersForCompanyNeed,
	fetchMatchingWorkersMetadataForCompanyNeed,
	fetchFlexibleMatchingWorkersForCompanyNeed,
	fetchPaginatedPoolWorkers,
	fetchOngoingProposalWorkers,
	fetchWorkersForCompanyNeed,
	fetchRejectedProfiles,
	fetchCandidates,
};
